import { NumberFormat, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { ReviewEntryDataFormSchema } from '@views/reviewEntries/types/getReviewEntryDataFormSchema'

import { useFormContext, useWatch } from '../../../../../../../../../../hooks'
import { getCurrentLocale } from '../../../../../../../../../../utils'
import { useReviewEntryLineItemTotals } from '../../hooks/useReviewEntryLineItemTotals'
import * as Styled from '../../styles'

export const ReviewEntryLineItemsTotals = (): ReactElement => {
  const { t } = useTranslation()
  const { control } = useFormContext<ReviewEntryDataFormSchema>()
  const currencyId = useWatch({ control, name: 'currencyId' })
  const { totalAmountExclVat, totalAmountInclVat, totalVatAmount } = useReviewEntryLineItemTotals()

  const formatAmount = (value: number) =>
    currencyId
      ? NumberFormat.getCurrencyNumber(value, currencyId, getCurrentLocale(), {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : value.toFixed(2)

  return (
    <Styled.ReviewEntryLineItemsTotalsWrapper>
      <Styled.ReviewEntryLineItemsTotalsLabel>
        <Text variant="bodyMD" weight="medium">
          {t('review_entries.detail.line_items.totals.label')}
          {currencyId ? ` (${currencyId})` : ''}
        </Text>
      </Styled.ReviewEntryLineItemsTotalsLabel>
      <Styled.ReviewEntryLineItemsTotalsAmount>
        <Text variant="bodyMD" weight="medium">
          {t('review_entries.detail.line_items.totals.excl_vat.label')}
        </Text>
        <Text variant="bodyLG">{formatAmount(totalAmountExclVat)}</Text>
      </Styled.ReviewEntryLineItemsTotalsAmount>
      <Styled.ReviewEntryLineItemsTotalsAmount>
        <Text variant="bodyMD" weight="medium">
          {t('review_entries.detail.line_items.totals.vat_amount.label')}
        </Text>
        <Text variant="bodyLG">{formatAmount(totalVatAmount)}</Text>
      </Styled.ReviewEntryLineItemsTotalsAmount>
      <Styled.ReviewEntryLineItemsTotalsAmount>
        <Text variant="bodyMD" weight="medium">
          {t('review_entries.detail.line_items.totals.incl_vat.label')}
        </Text>
        <Text variant="bodyLG" weight="semiBold">
          {formatAmount(totalAmountInclVat)}
        </Text>
      </Styled.ReviewEntryLineItemsTotalsAmount>
    </Styled.ReviewEntryLineItemsTotalsWrapper>
  )
}
